// External Libraries
import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';

// Assets
import LeftArrow from '../assets/left-arrow.png';
import Clock from '../assets/clock.png';
import LinkIcon from '../assets/link-icon.png';
import Edit from '../assets/edit.png';
import Delete from '../assets/delete.png';
import Spark from '../assets/spark.png';
import FollowingIdea from '../assets/tick.png';

// Styles
import '../styles/IdeaDetails.css';

// Constants
import { APPROVED } from '../constants/Constants';

// Redux/State Management
import { RootState } from '../redux/store';

// API Services
import {
  deleteIdeaAPI,
  deleteUnFollowAPI,
  getIdeaDetails,
  postFollowAPI,
} from '../api/IdeaService';

// Types
import {
  EmailThreadProps,
  IdeaDetailsResponse,
  IPStatusProps,
} from '../types/types';

// Utilities
import { formatDate, replaceNewlinesWithBr } from '../utils/CommonUtils';

// Components
import { ProfileCard } from './common/ProfileCard';
import Button from './common/Button';
import { RedirectLink } from './common/RedirectLink';
import Loading from './common/Loading';
import Conversation from './Conversation';
import Text from './common/Text';
import ActionConfirmationModal from './common/ActionConfirmationModal';

const IdeaDetails: React.FC = () => {
  const reduxUser = useSelector((state: RootState) => state.user);
  const loggedInUserEmail = reduxUser?.userDetails?.email;
  const reduxsupportAndPartnerships = useSelector(
    (state: RootState) => state.support_or_partnership,
  );
  const reduxIpStatuses = useSelector((state: RootState) => state.ip_statuses);
  const [ipStatus, setIpStatus] = useState<string>('');
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [names, setNames] = useState<string[]>([]);
  const ideaId = id ? parseInt(id, 10) : undefined;
  const [canEdit, setCanEdit] = useState<boolean | null>(null);
  const [isPopupVisible, setPopupVisible] = useState(false);
  const [conversationDetails, setConversationDetails] =
    useState<EmailThreadProps | null>(null);
  const [isFollowState, setIsFollowState] = useState<boolean | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formattedPatents, setFormattedPatents] = useState<string | null>('');
  const [isButtonDisabled, setIsButtonDisabled] = useState<boolean>(false);

  const handleContactClick = (email: string, recipientName: string) => {
    setConversationDetails({
      emailAddress: email,
      recipientName: recipientName,
    });
    setPopupVisible(true);
  };

  const handleBlurClick = () => {
    setPopupVisible(false);
    setConversationDetails(null);
  };

  const [idea, setIdea] = useState<IdeaDetailsResponse>({
    introduction: '',
    created_at: '',
    title: '',
    idea_patents: [],
    ip_status: null,
    principal_investigator: {
      email: '',
      first_name: '',
      last_name: '',
      id: null,
      position: '',
      profile_image_url: '',
      user_type_id: null,
      admin_status_id: null,
      user_status_id: null,
    },
    case_manager: undefined,
    opportunity: '',
    problem: '',
    urls: [],
    support_ids: [],
    tenant_id: null,
    user_id: null,
    is_following: null,
    id: null,
    ip_status_id: null,
    patents: [],
  });

  useEffect(() => {
    const fetchIdeaDetails = async () => {
      if (ideaId !== undefined) {
        const userId = reduxUser.userDetails?.userId;
        const result = await getIdeaDetails(ideaId, userId);
        if ('code' in result) {
          console.error('Error fetching the idea details:', result);
          setMessage(result.message);
          setLoading(false);
        } else {
          const resIdea = result as IdeaDetailsResponse;
          setNames(
            resIdea.support_ids
              .map((id) => {
                const partner =
                  reduxsupportAndPartnerships.support_or_partnership.find(
                    (item) => item.id === id,
                  );
                return partner ? partner.name : '';
              })
              .filter((name) => name !== ''),
          );
          const status = reduxIpStatuses.ip_statuses.find(
            (item) => item.id === resIdea.ip_status || null,
          );
          setIpStatus(status ? status.name : '');
          setIdea({
            introduction: resIdea.introduction,
            created_at: formatDate(resIdea.created_at),
            title: resIdea.title,
            opportunity: resIdea.opportunity,
            idea_patents: resIdea.idea_patents,
            ip_status: resIdea.ip_status,
            problem: resIdea.problem,
            urls: resIdea.urls,
            principal_investigator: {
              email: resIdea.principal_investigator.email,
              first_name: resIdea.principal_investigator.first_name,
              last_name: resIdea.principal_investigator.last_name,
              id: resIdea.principal_investigator.id,
              position: resIdea.principal_investigator.position,
              profile_image_url:
                resIdea.principal_investigator.profile_image_url,
              user_type_id: resIdea.principal_investigator.user_type_id,
              admin_status_id: resIdea.principal_investigator.admin_status_id,
              user_status_id: resIdea.principal_investigator.user_status_id,
            },
            case_manager: resIdea.case_manager
              ? {
                  email: resIdea.case_manager.email,
                  first_name: resIdea.case_manager.first_name,
                  last_name: resIdea.case_manager.last_name,
                  id: resIdea.case_manager.id,
                  position: resIdea.case_manager.position,
                  profile_image_url: resIdea.case_manager.profile_image_url,
                  user_type_id: resIdea.case_manager.user_type_id,
                  admin_status_id: resIdea.case_manager.admin_status_id,
                  user_status_id: resIdea.case_manager.user_status_id,
                }
              : undefined,
            support_ids: resIdea.support_ids,
            tenant_id: resIdea.tenant_id,
            user_id: resIdea.user_id,
            is_following: resIdea.is_following,
            id: resIdea.id,
            ip_status_id: resIdea.ip_status_id,
            patents: resIdea.patents,
          });
          setIsFollowState(resIdea.is_following ?? null);
          if (
            ideaId &&
            reduxUser.userDetails?.userId ===
              resIdea.principal_investigator.id &&
            resIdea.principal_investigator &&
            resIdea.case_manager
              ? resIdea.case_manager.id === reduxUser.userDetails?.userId
                ? true
                : false
              : resIdea.case_manager?.id === reduxUser.userDetails?.userId ||
                  resIdea.principal_investigator.id ===
                    reduxUser.userDetails?.userId
                ? true
                : false
          ) {
            setCanEdit(true);
          } else {
            setCanEdit(false);
          }
          setLoading(false);
          setMessage(null);
        }
      }
    };
    fetchIdeaDetails();
  }, []);

  const handleFollowIdea = async () => {
    if (isFollowState) {
      if (reduxUser.userDetails?.userId !== undefined && idea.id !== null) {
        setIsFollowState((prevState) => !prevState);
        const unFollowRespose = await deleteUnFollowAPI(
          idea.id,
          reduxUser.userDetails?.userId,
        );
        if ('code' in unFollowRespose) {
          console.error('Error unfollow the idea:', unFollowRespose);
        } else {
          if (unFollowRespose) {
            // setIsFollowState((prevState) => !prevState);
          } else {
            console.log('probelm with unfollow this idea');
          }
        }
      }
    } else {
      if (reduxUser.userDetails?.userId !== undefined && idea.id !== null) {
        setIsFollowState((prevState) => !prevState);
        const followedRespose = await postFollowAPI(
          idea.id,
          reduxUser.userDetails?.userId,
        );
        if ('code' in followedRespose) {
          console.error('Error follow the idea:', followedRespose);
        } else {
          if (followedRespose) {
            // setIsFollowState((prevState) => !prevState);
          } else {
            console.log('probelm with follow this idea');
          }
        }
      }
    }
  };

  const IPStatus: React.FC<IPStatusProps> = ({ idea_patents, ip_status }) => {
    useEffect(() => {
      if (idea_patents.length > 1) {
        const joinedRes = idea_patents.join(' • ');
        setFormattedPatents(joinedRes);
      } else if (idea_patents.length === 1) {
        setFormattedPatents(idea_patents[0]);
      } else {
        setFormattedPatents('');
      }
    }, []);
    return (
      <div className="partnership-needed">
        {ip_status && (
          <>
            <Text className="heading">IP Status</Text>
            <Text className="seekings">{ip_status || null}</Text>
          </>
        )}
        {formattedPatents && (
          <Text className="patent-text">
            <strong>Patent number:</strong> {formattedPatents}
          </Text>
        )}
      </div>
    );
  };

  const handleDeleteIdea = async () => {
    setIsButtonDisabled(true);
    if (ideaId !== undefined && reduxUser.userDetails?.userId !== undefined) {
      // we have invoke the delete idea api here
      const deleteIdeaRes = await deleteIdeaAPI(
        ideaId,
        reduxUser.userDetails?.userId,
      );
      if ('code' in deleteIdeaRes) {
        console.error('Error delete the idea:', deleteIdeaRes);
        setError(deleteIdeaRes.message);
      } else {
        if (deleteIdeaRes) {
          navigate('/my-ideas');
          setIsModalOpen(false);
          setError(null);
        } else {
          console.log('problem with delete this idea');
        }
      }
    }
  };

  const handleCancel = () => {
    setIsModalOpen(false);
    setError(null);
  };

  return (
    <>
      {loading ? (
        <div className="loading-class">
          <Loading color="var(--black)" />
        </div>
      ) : message === null ? (
        <div className="idea-details">
          <div className="idea-details-card">
            <div className="idea-details-title-container">
              {canEdit ? (
                <div className="idea-nav-bar">
                  <Button
                    type="button"
                    className="nav-button"
                    onClick={() => navigate(-1)}
                    aria-label="ideas navigation"
                  >
                    <img
                      src={LeftArrow}
                      alt="left-icon"
                      className="left-arrow-icon"
                    />
                  </Button>

                  <div className="modify-section">
                    <Button
                      type="button"
                      className="delete"
                      onClick={() => setIsModalOpen(true)}
                    >
                      <img src={Delete} alt="delete" className="edit-icon" />
                      <div className="input-label">Delete</div>
                    </Button>
                    <Button
                      type="button"
                      className="edit"
                      onClick={() => navigate(`/my-ideas/edit/${id}`)}
                    >
                      <img src={Edit} alt="edit" className="edit-icon" />
                      <div className="input-label">Edit</div>
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="idea-nav-bar">
                  <Button
                    type="button"
                    className="nav-button"
                    onClick={() => navigate(-1)}
                    aria-label="ideas navigation"
                  >
                    <img
                      src={LeftArrow}
                      alt="left-icon"
                      className="left-arrow-icon"
                    />
                  </Button>
                  <div className="idea">
                    <img src={Spark} alt="spark" className="spark-icon" />
                    <div>idea</div>
                  </div>
                </div>
              )}

              <div className="display-extra-small">{idea.title}</div>
              <div className="posted-date">
                <img src={Clock} alt="clock-icon" className="clock-icon" />
                <Text className="small-medium">
                  Posted on {idea.created_at}
                </Text>
              </div>
            </div>
            {(names.length !== 0 ||
              idea.idea_patents.length > 0 ||
              ipStatus) && (
              <>
                <div className="line" />
                <div className="partnership-info">
                  {names.length !== 0 && (
                    <div className="partnership-needed">
                      <Text className="heading">Seeking</Text>
                      <Text className="seekings">{names.join(', ')}</Text>
                    </div>
                  )}

                  {(idea.idea_patents.length > 0 || ipStatus) && (
                    <IPStatus
                      idea_patents={idea.idea_patents}
                      ip_status={ipStatus}
                    />
                  )}
                </div>
              </>
            )}
            <div className="idea-details-container">
              <div className="line" />
              {idea.introduction && (
                <section className="idea-details-section-container">
                  <Text className="heading">Introduction</Text>
                  <Text className="medium-grey">{idea.introduction}</Text>
                </section>
              )}

              {idea.problem && (
                <section className="idea-details-section-container">
                  <Text className="heading">Problem</Text>
                  <div
                    className="medium-grey"
                    dangerouslySetInnerHTML={{
                      __html: replaceNewlinesWithBr(idea.problem),
                    }}
                  />
                </section>
              )}

              {idea.opportunity && (
                <section className="idea-details-section-container">
                  <Text className="heading">Opportunity</Text>
                  <div
                    className="medium-grey"
                    dangerouslySetInnerHTML={{
                      __html: replaceNewlinesWithBr(idea.opportunity),
                    }}
                  />
                </section>
              )}

              {idea.urls?.length > 0 && (
                <div className="buttons">
                  {idea.urls?.map((data) => (
                    <a
                      href={data?.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      key={data?.url}
                    >
                      <RedirectLink text={data?.title || ''} icon={LinkIcon} />
                    </a>
                  ))}
                </div>
              )}

              {canEdit ? (
                <div className="idea-follow-button-container">
                  <Button type="button" className="disabled-button">
                    {isFollowState ? 'Following' : 'Follow this idea'}
                  </Button>
                </div>
              ) : (
                <div className="idea-follow-button-container">
                  <Button
                    type="button"
                    onClick={handleFollowIdea}
                    className={`idea-button ${
                      isFollowState
                        ? 'idea-unfollow-button'
                        : 'idea-follow-button'
                    }`}
                  >
                    {isFollowState ? (
                      <>
                        <img
                          src={FollowingIdea}
                          alt="following-idea"
                          className="following-idea-img"
                        />
                        Following
                      </>
                    ) : (
                      'Follow this idea'
                    )}
                  </Button>
                </div>
              )}
            </div>
          </div>
          {(idea.case_manager || idea.principal_investigator) && (
            <div className="idea-contacts-container">
              <Text className="idea-owner">Idea Contacts</Text>
              <div className="line" />

              {idea.principal_investigator && (
                <ProfileCard
                  name={
                    idea.principal_investigator?.first_name +
                    ' ' +
                    idea.principal_investigator.last_name
                  }
                  position={idea.principal_investigator.position}
                  photo={idea.principal_investigator?.profile_image_url}
                  userType={
                    idea.principal_investigator?.user_type_id ?? undefined
                  }
                  ideaOwnerId={idea.principal_investigator?.id ?? undefined}
                  principalInvestigator={idea.principal_investigator !== null}
                />
              )}

              {idea.case_manager && (
                <ProfileCard
                  name={
                    idea.case_manager?.first_name +
                    ' ' +
                    idea.case_manager.last_name
                  }
                  position={idea.case_manager.position}
                  photo={idea.case_manager?.profile_image_url}
                  userType={idea.case_manager?.user_type_id ?? undefined}
                  ideaOwnerId={idea.case_manager?.id ?? undefined}
                  caseManager={idea.case_manager !== null}
                />
              )}
              {((idea.case_manager?.admin_status_id === APPROVED &&
                idea.case_manager?.user_status_id === APPROVED &&
                idea.case_manager?.email !== loggedInUserEmail) ||
                (idea.principal_investigator?.admin_status_id === APPROVED &&
                  idea.principal_investigator?.user_status_id === APPROVED &&
                  idea.principal_investigator?.email !==
                    loggedInUserEmail)) && (
                <div className="idea-contact-button">
                  <Button
                    type="button"
                    onClick={() =>
                      handleContactClick(
                        idea.case_manager?.email ||
                          idea.principal_investigator?.email ||
                          '',
                        `${idea.case_manager?.first_name || idea.principal_investigator?.first_name || ''} ${idea.case_manager?.last_name || idea.principal_investigator?.last_name || ''}`,
                      )
                    }
                    className={
                      idea.case_manager?.email === loggedInUserEmail ||
                      idea.principal_investigator?.email === loggedInUserEmail
                        ? 'disabled-button'
                        : 'tertiary-button'
                    }
                    disabled={
                      idea.case_manager?.email === loggedInUserEmail ||
                      idea.principal_investigator?.email === loggedInUserEmail
                    }
                  >
                    Start a Conversation
                  </Button>
                </div>
              )}
            </div>
          )}

          {isPopupVisible && (
            <>
              <div
                className="filter-container contact-bg-color"
                onClick={handleBlurClick}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    handleBlurClick();
                  }
                }}
              >
                <div className="bottom-popup">
                  {conversationDetails && (
                    <Conversation
                      emailAddress={conversationDetails.emailAddress}
                      recipientName={conversationDetails.recipientName}
                    />
                  )}
                </div>
              </div>
            </>
          )}
          <ActionConfirmationModal
            title="Are you sure you want to delete this idea?"
            message="Deleting this idea will remove it permanently from your profile, and all associated data will be deleted. This action cannot be undone."
            isFirstButtonRequired={true}
            isFirstButtonDisabled={isButtonDisabled}
            onFirstButtonAction={handleDeleteIdea}
            onFirstButtonName="Delete Idea"
            isSecondButtonRequired={true}
            onSecondButtonAction={handleCancel}
            onSecondButtonName="Cancel"
            isOpen={isModalOpen}
            error={error}
          />
        </div>
      ) : (
        <div className="loading-class">{message}</div>
      )}
    </>
  );
};

export default IdeaDetails;
